/**
 * `iterator-walk` (`LN-WGT02`, the shape ruled in the `LN-STL-M0` review): an
 * iterator stepping from `begin()` to `end()`, the past-the-end slot drawn as
 * an empty box the loop never reads.
 *
 * <div class="pc-widget" data-widget="iterator-walk"
 *      data-walks="[{&quot;label&quot;:&quot;std::vector&lt;int&gt; c&quot;,&quot;values&quot;:[72,45],&quot;layout&quot;:&quot;contiguous&quot;},
 *                   {&quot;label&quot;:&quot;std::deque&lt;int&gt; c&quot;,&quot;values&quot;:[72,45],&quot;layout&quot;:&quot;blocks&quot;,&quot;blockSize&quot;:1}]"
 *      data-code="for (auto it = c.begin(); it != c.end(); ++it) std::cout &lt;&lt; *it;"
 *      data-caption="begin() on 72, then 45, then end().">
 *   <p class="pc-widget-fallback">The iterator stops on 72, then 45, then reaches end().</p>
 * </div>
 *
 * `data-walks` (required): a JSON list of walks played one after the other,
 * each `{ label, values, layout, blockSize? }`: `label` the container (code),
 * `values` its elements, `layout` `"contiguous"` (one block) or `"blocks"`
 * (fixed blocks of `blockSize` elements, required then, the hop between blocks
 * drawn as a gap). Every walk is `values.length + 1` steps: one per element,
 * `begin()` first, and the past-the-end slot last. `data-code` (optional) is
 * one line shown above every walk; `data-caption` (optional). The first frame
 * is the first walk with the iterator on `begin()`. The invalidated iterator
 * M11 and M17 need is a later optional per-walk key; this markup stays valid.
 */

import {
  asText,
  attachStepper,
  buildShell,
  el,
  isNonEmptyString,
  isRecord,
  isStringOrNumber,
  liveNote,
  parseJson,
  type WidgetText,
} from "./dom";

interface Walk {
  label: string;
  values: (string | number)[];
  layout: "contiguous" | "blocks";
  blockSize?: number;
}

const isWalks = (value: unknown): value is Walk[] =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every(
    (walk) =>
      isRecord(walk) &&
      isNonEmptyString(walk.label) &&
      Array.isArray(walk.values) &&
      walk.values.length > 0 &&
      walk.values.every(isStringOrNumber) &&
      (walk.layout === "contiguous" ||
        (walk.layout === "blocks" && Number.isInteger(walk.blockSize) && (walk.blockSize as number) > 0)),
  );

export function renderIteratorWalk(host: HTMLElement, text: WidgetText): boolean {
  const walks = parseJson(host.dataset.walks, isWalks);
  if (!walks) return false;

  const shell = buildShell(host, {
    label: text("labelIterator"),
    code: host.dataset.code,
    caption: host.dataset.caption,
  });

  // Every step is a (walk, position) pair; position values.length is end().
  const positions: [number, number][] = walks.flatMap((walk, w) =>
    Array.from({ length: walk.values.length + 1 }, (_, i): [number, number] => [w, i]),
  );

  const title = el("p", "pc-iter-title");
  const memory = el("div", "pc-iter-memory");
  const note = liveNote();
  shell.body.append(title, memory, note);

  let drawn = -1;
  let cells: HTMLDivElement[] = [];

  const drawWalk = (w: number) => {
    const walk = walks[w];
    title.textContent = "";
    title.append(el("code", undefined, walk.label));
    if (walks.length > 1) title.append(el("span", "pc-iter-count", text("iterWalk", { n: w + 1, total: walks.length })));

    memory.textContent = "";
    cells = [];
    const size = walk.layout === "blocks" ? (walk.blockSize ?? walk.values.length) : walk.values.length;
    let row: HTMLDivElement | null = null;
    walk.values.forEach((value, i) => {
      if (i % size === 0) {
        const block = el("div", "pc-iter-block");
        if (walk.layout === "blocks") block.append(el("span", "pc-vec-block-label", text("iterBlock", { n: i / size + 1 })));
        row = el("div", "pc-widget-cells pc-widget-cells--tight");
        block.append(row);
        memory.append(block);
      }
      const cell = el("div", "pc-cell");
      cell.append(el("span", "pc-cell-value", asText(value)));
      cell.append(el("span", "pc-cell-meta", i === 0 ? "begin()" : ""));
      cell.append(el("span", "pc-cell-cursor"));
      row?.append(cell);
      cells.push(cell);
    });
    const end = el("div", "pc-iter-block");
    const endRow = el("div", "pc-widget-cells pc-widget-cells--tight");
    const slot = el("div", "pc-cell is-spare pc-iter-end");
    slot.append(el("span", "pc-cell-value", ""));
    slot.append(el("span", "pc-cell-meta", "end()"));
    slot.append(el("span", "pc-cell-cursor"));
    endRow.append(slot);
    end.append(endRow);
    memory.append(end);
    cells.push(slot);
    drawn = w;
  };

  attachStepper(
    shell,
    positions.length,
    (step) => {
      const [w, at] = positions[step];
      if (w !== drawn) drawWalk(w);
      cells.forEach((cell, i) => {
        cell.classList.toggle("is-active", i === at);
        const cursor = cell.querySelector(".pc-cell-cursor");
        if (cursor) cursor.textContent = i === at ? "it" : "";
      });
      const values = walks[w].values;
      note.textContent = at < values.length ? text("iterAt", { value: asText(values[at]) }) : text("iterEnd");
    },
    text,
    { liveReadout: false },
  );
  return true;
}
